import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchAllArticles } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';
import { CardBody, CardContainer, CardItem } from '../ui/3d-card';

const ArticleSection = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width <= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadLatestArticles = async () => {
      try {
        setLoading(true);
        const articlesData = await fetchAllArticles(1, 6);
        setArticles(articlesData.articles || []);
      } catch (err) {
        console.error('Error loading articles:', err);
        setError(language === 'MR' ? 'लेख लोड करण्यात अयशस्वी' : 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    loadLatestArticles();
  }, [language]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options).toUpperCase();
  };

  const getImageSrc = (thumbnail) => {
    if (!thumbnail) return '/api/placeholder/600/400';
    if (thumbnail.startsWith('data:')) return thumbnail;
    if (thumbnail.startsWith('http')) return thumbnail;
    if (thumbnail.startsWith('uploads/')) {
      return `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500'}/${thumbnail}`;
    }
    return '/api/placeholder/600/400';
  };

  if (loading) {
    return (
      <section className="py-4 md:py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center mb-2 md:mb-4">
            <div className={`hidden md:block flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800`}></div>
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-red-800 mx-0 md:mx-6 my-3 md:my-0">
                {language === 'MR' ? 'शब्दांची फुले' : 'Stories in Bloom'}
              </h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'संवेदनशील लेख आणि कथा' : 'Thoughtful articles and compelling stories'}
              </p>
            </div>
            <div className={`hidden md:block flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent`}></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 justify-items-center">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse w-full max-w-[350px]">
                <div className="bg-gray-300 rounded-xl h-48 sm:h-56 md:h-60 lg:h-64 xl:h-72 mb-4"></div>
                <div className="h-4 bg-gray-300 mb-2 w-3/4"></div>
                <div className="h-3 bg-gray-300 w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-4 md:py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center mb-2 md:mb-4">
            <div className={`hidden md:block flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800`}></div>
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-red-800 mx-0 md:mx-6 my-3 md:my-0">
                {language === 'MR' ? 'शब्दांची फुले' : 'Stories in Bloom'}
              </h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'संवेदनशील लेख आणि कथा' : 'Thoughtful articles and compelling stories'}
              </p>
            </div>
            <div className={`hidden md:block flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent`}></div>
          </div>
          <div className="bg-red-50 border-2 border-red-100 rounded-xl p-4 md:p-6 max-w-md mx-auto">
            <div className="text-3xl md:text-4xl text-red-800 mb-2 md:mb-3">⚠</div>
            <p className="text-black font-semibold text-xs md:text-sm">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 md:py-4 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center mb-4 md:mb-4">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`hidden md:block flex-1 h-1 bg-gradient-to-r from-transparent via-red-800 to-red-800`}
          />
          <div className="text-center">
            <motion.h2 
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-red-800 mx-0 md:mx-6 my-3 md:my-0 uppercase tracking-wider"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {language === 'MR' ? 'शब्दांची फुले' : 'Stories in Bloom'}
            </motion.h2>
            <p className="text-sm text-gray-600 mt-2 italic">
              {language === 'MR' ? 'संवेदनशील लेख आणि कथा' : 'Thoughtful articles and compelling stories'}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`hidden md:block flex-1 h-1 bg-gradient-to-l from-transparent via-red-800 to-red-800`}
          />
        </div>

        {/* 3D Cards Grid - Centered for all devices */}
        <div className={`grid grid-cols-1 ${
          isTablet ? 'grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
        } gap-6 md:gap-8 justify-items-center`}>
          {articles.map((article, index) => (
            <motion.div
              key={article._id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="w-full max-w-[350px]"
            >
              <CardContainer className="inter-var w-full">
                <CardBody className={`bg-white relative group/card hover:shadow-2xl hover:shadow-red-800/[0.2] border-red-100 w-full ${
                  isTablet ? 'h-[380px]' : 'h-[380px] md:h-[420px]'
                } rounded-2xl p-4 md:p-6 border-2 hover:border-red-200 transition-all duration-300`}>
                  
                  {/* Title */}
                  <CardItem
                    translateZ="50"
                    className={`font-bold text-black line-clamp-2 mb-2 md:mb-3 hover:text-red-800 transition-colors duration-200 ${
                      isTablet ? 'text-base' : 'text-lg md:text-xl'
                    }`}
                  >
                    {article.title}
                  </CardItem>

                  {/* Date */}
                  <CardItem
                    as="p"
                    translateZ="60"
                    className={`text-gray-600 font-medium mb-3 md:mb-4 uppercase tracking-wide ${
                      isTablet ? 'text-xs' : 'text-xs md:text-sm'
                    }`}
                  >
                    {formatDate(article.date)}
                  </CardItem>

                  {/* Image */}
                  <CardItem translateZ="100" rotateX={20} rotateZ={-10} className="w-full">
                    <img
                      src={getImageSrc(article.thumbnail)}
                      height="200"
                      width="350"
                      className={`w-full object-cover rounded-xl group-hover/card:shadow-2xl border border-red-100 transition-all duration-500 group-hover/card:scale-[1.02] ${
                        isTablet ? 'h-40' : 'h-40 md:h-52'
                      }`}
                      alt={article.title}
                    />
                  </CardItem>

                  {/* Action Buttons */}
                  <div className={`flex justify-between items-center ${
                    isTablet ? 'mt-4' : 'mt-6 md:mt-8'
                  }`}>
                    <CardItem
                      translateZ={20}
                      translateX={-25}
                      as={Link}
                      to={`/article/${article.slug || article._id}`}
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl font-semibold text-black hover:text-white hover:bg-red-800 border-2 border-red-800 transition-all duration-300 flex items-center gap-1 md:gap-2 uppercase tracking-wide ${
                        isTablet ? 'text-xs' : 'text-xs md:text-sm'
                      }`}
                    >
                      {language === 'MR' ? 'वाचा' : 'Read'} <FiArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                    </CardItem>
                    <CardItem
                      translateZ={20}
                      translateX={25}
                      as="button"
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl bg-red-800 hover:bg-black text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl uppercase tracking-wide ${
                        isTablet ? 'text-xs' : 'text-xs md:text-sm'
                      }`}
                      onClick={() => {
                        navigator.share && navigator.share({
                          title: article.title,
                          url: window.location.origin + `/article/${article._id}`
                        });
                      }}
                    >
                      {language === 'MR' ? 'शेअर' : 'Share'}
                    </CardItem>
                  </div>
                </CardBody>
              </CardContainer>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ArticleSection;